import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { api } from "@/lib/api";

type Product = {
  id: number;
  sku: string;
  name: string;
  barcode: string | null;
  pos_variation_id: string | null;
};

type Member = {
  product_id: number;
  units: number;
  sku: string | null;
  name: string | null;
  barcode: string | null;
};

type Group = {
  id: number;
  code: string;
  name: string;
  unit_label: string;
  description: string | null;
  members: Member[];
  member_count: number;
};

export function ProductGroupsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Group | null>(null);
  const [creating, setCreating] = useState(false);

  const groupsQ = useQuery({
    queryKey: ["product-groups"],
    queryFn: async () => (await api.get<Group[]>("/product-groups")).data,
  });

  const diagQ = useQuery({
    queryKey: ["product-groups-diagnostics"],
    queryFn: async () =>
      (
        await api.get<{
          untracked_products: { id: number; sku: string; name: string; times_sold: number }[];
          untracked_count: number;
          ambiguous_names: {
            name: string; n_products: number; skus: string;
            group_assignments: string | null;
          }[];
          ambiguous_count: number;
        }>("/product-groups/diagnostics")
      ).data,
  });

  const del = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/product-groups/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-groups"] }),
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Nhóm sản phẩm</h1>
          <p className="text-sm text-slate-500 mt-0.5">Gom SP thành nhóm để tính kho theo unit thực.</p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="btn-primary"
        >
          + Tạo nhóm
        </button>
      </div>

      <div className="text-sm text-gray-600 mb-4">
        Gom nhiều phân loại sản phẩm vào 1 nhóm với hệ số đơn vị. Ví dụ: nhóm
        "Đệm lót ghế ngồi" gồm "1 ĐỆM" (units=1), "2 ĐỆM" (units=2), "3 ĐỆM" (units=3)
        → bán 1 đơn 2 ĐỆM = trừ kho 2 đệm thực.
      </div>

      {/* Diagnostics warning */}
      {diagQ.data && diagQ.data.untracked_count > 0 && (
        <details className="mb-4 bg-yellow-50 border border-yellow-300 rounded p-3 text-sm">
          <summary className="cursor-pointer font-semibold text-yellow-800">
            ⚠️ {diagQ.data.untracked_count} sản phẩm CHƯA gán nhóm
            {diagQ.data.ambiguous_count > 0 &&
              ` + ${diagQ.data.ambiguous_count} tên trùng (đa shop)`}
            <span className="text-xs text-gray-600 ml-2">(click để xem)</span>
          </summary>
          <div className="mt-3">
            <table className="w-full text-xs">
              <thead className="bg-yellow-100">
                <tr>
                  <th className="text-left px-2 py-1">SKU</th>
                  <th className="text-left px-2 py-1">Tên</th>
                  <th className="text-right px-2 py-1">Số lần bán</th>
                </tr>
              </thead>
              <tbody>
                {diagQ.data.untracked_products.slice(0, 30).map((p) => (
                  <tr key={p.id} className="border-t border-yellow-200">
                    <td className="px-2 py-1 font-mono text-[10px]">{p.sku}</td>
                    <td className="px-2 py-1">{p.name}</td>
                    <td className="px-2 py-1 text-right">{p.times_sold}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {diagQ.data.untracked_products.length > 30 && (
              <div className="text-xs text-gray-500 mt-1">
                + {diagQ.data.untracked_products.length - 30} sản phẩm khác...
              </div>
            )}
          </div>
          {diagQ.data.ambiguous_count > 0 && (
            <div className="mt-3 border-t border-yellow-300 pt-2">
              <div className="font-semibold text-yellow-800 mb-1">
                Tên trùng giữa các shop:
              </div>
              {diagQ.data.ambiguous_names.map((a, i) => (
                <div key={i} className="text-[11px]">
                  <b>{a.name}</b> ({a.n_products} SP): {a.skus}{" "}
                  {a.group_assignments && (
                    <span className="text-gray-600">
                      → {a.group_assignments}
                    </span>
                  )}
                </div>
              ))}
              <div className="text-[10px] text-gray-600 mt-1 italic">
                ⚠️ Nếu cùng tên ở các shop nhưng khác nhóm/units → name fallback sẽ bị bỏ qua
                (để tránh sai). Phải gán đúng variation_id (qua Add từng SP).
              </div>
            </div>
          )}
        </details>
      )}

      <div className="bg-white border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2">Mã</th>
              <th className="text-left px-3 py-2">Tên nhóm</th>
              <th className="text-left px-3 py-2">Đơn vị</th>
              <th className="text-right px-3 py-2">Số phân loại</th>
              <th className="text-left px-3 py-2">Thành viên</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(groupsQ.data ?? []).map((g) => (
              <tr key={g.id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-xs">{g.code}</td>
                <td className="px-3 py-2 font-medium">{g.name}</td>
                <td className="px-3 py-2">{g.unit_label}</td>
                <td className="px-3 py-2 text-right">{g.member_count}</td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {g.members.slice(0, 3).map((m) => (
                    <div key={m.product_id}>
                      <b>{m.units}×</b> {m.name}
                    </div>
                  ))}
                  {g.members.length > 3 && (
                    <div className="text-gray-400">+{g.members.length - 3} khác</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    onClick={() => setEditing(g)}
                    className="text-blue-600 hover:underline mr-2"
                  >
                    Sửa
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Xoá nhóm "${g.name}"?`)) del.mutate(g.id);
                    }}
                    className="text-red-600 hover:underline"
                  >
                    Xoá
                  </button>
                </td>
              </tr>
            ))}
            {groupsQ.data?.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-gray-500">
                  Chưa có nhóm nào. Bấm "+ Tạo nhóm" để bắt đầu.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <GroupEditor
          group={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["product-groups"] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function GroupEditor({
  group,
  onClose,
  onSaved,
}: {
  group: Group | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [code, setCode] = useState(group?.code ?? "");
  const [name, setName] = useState(group?.name ?? "");
  const [unitLabel, setUnitLabel] = useState(group?.unit_label ?? "cái");
  const [description, setDescription] = useState(group?.description ?? "");
  const [members, setMembers] = useState<Member[]>(group?.members ?? []);
  const [search, setSearch] = useState("");

  const productsQ = useQuery({
    queryKey: ["products-list-all"],
    queryFn: async () =>
      (await api.get<Product[]>("/products", { params: { limit: 2000 } })).data,
  });

  const memberIds = useMemo(
    () => new Set(members.map((m) => m.product_id)),
    [members]
  );

  const filteredProducts = useMemo(() => {
    const all = productsQ.data ?? [];
    const s = search.trim().toLowerCase();
    if (!s) return all.slice(0, 50);
    return all
      .filter(
        (p) =>
          p.name.toLowerCase().includes(s) ||
          p.sku.toLowerCase().includes(s) ||
          (p.barcode || "").toLowerCase().includes(s)
      )
      .slice(0, 50);
  }, [productsQ.data, search]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        code: code.trim(),
        name: name.trim(),
        unit_label: unitLabel.trim() || "cái",
        description: description.trim() || null,
      };
      let g: Group;
      if (group) {
        g = (await api.put<Group>(`/product-groups/${group.id}`, body)).data;
      } else {
        g = (await api.post<Group>("/product-groups", body)).data;
      }
      // Save members
      await api.put(`/product-groups/${g.id}/members`, {
        members: members.map((m) => ({
          product_id: m.product_id,
          units: m.units,
        })),
      });
      return g;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product-groups"] });
      qc.invalidateQueries({ queryKey: ["s2a"] });
      qc.invalidateQueries({ queryKey: ["s2a-day-detail"] });
      onSaved();
    },
  });

  const addProduct = (p: Product) => {
    if (memberIds.has(p.id)) return;
    setMembers([
      ...members,
      { product_id: p.id, units: 1, sku: p.sku, name: p.name, barcode: p.barcode },
    ]);
  };

  const updateUnits = (idx: number, units: number) => {
    setMembers(members.map((m, i) => (i === idx ? { ...m, units } : m)));
  };

  const removeMember = (idx: number) => {
    setMembers(members.filter((_, i) => i !== idx));
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-end z-50">
      <div className="bg-white w-full max-w-3xl h-full overflow-auto p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4 border-b pb-2">
          <h2 className="text-lg font-semibold">
            {group ? "Sửa nhóm" : "Tạo nhóm mới"}
          </h2>
          <button onClick={onClose} className="text-gray-500 text-xl hover:text-gray-900">
            ✕
          </button>
        </div>

        <div className="space-y-3 mb-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">
                Mã nhóm <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="DEM_LOT"
                className="w-full border rounded px-2 py-1.5 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">
                Đơn vị (kho)
              </label>
              <input
                type="text"
                value={unitLabel}
                onChange={(e) => setUnitLabel(e.target.value)}
                placeholder="đệm, gói, hộp..."
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">
              Tên nhóm <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Đệm lót ghế ngồi"
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Ghi chú</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </div>
        </div>

        <div className="border-t pt-4">
          <h3 className="text-sm font-semibold mb-2">
            Thành viên ({members.length})
          </h3>
          {members.length > 0 && (
            <div className="bg-gray-50 rounded mb-3 max-h-72 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">SKU</th>
                    <th className="text-left px-2 py-1">Tên</th>
                    <th className="text-right px-2 py-1 w-20">Units / sp</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m, i) => (
                    <tr key={m.product_id} className="border-t">
                      <td className="px-2 py-1 font-mono">{m.sku}</td>
                      <td className="px-2 py-1">{m.name}</td>
                      <td className="px-2 py-1 text-right">
                        <input
                          type="number"
                          min={1}
                          value={m.units}
                          onChange={(e) => updateUnits(i, Number(e.target.value) || 1)}
                          className="w-16 border rounded px-1 py-0.5 text-right"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <button
                          onClick={() => removeMember(i)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-2">
            <label className="block text-xs text-gray-600 mb-0.5">Thêm sản phẩm</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm SKU / tên / barcode..."
              className="w-full border rounded px-2 py-1.5 text-sm mb-2"
            />
            <div className="border rounded max-h-60 overflow-auto">
              <table className="w-full text-xs">
                <tbody>
                  {filteredProducts.map((p) => {
                    const added = memberIds.has(p.id);
                    return (
                      <tr key={p.id} className="border-t hover:bg-blue-50">
                        <td className="px-2 py-1 font-mono text-[10px]">{p.sku}</td>
                        <td className="px-2 py-1">{p.name}</td>
                        <td className="px-2 py-1 text-right w-20">
                          <button
                            onClick={() => addProduct(p)}
                            disabled={added}
                            className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded disabled:bg-gray-300"
                          >
                            {added ? "Đã add" : "+ Add"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-5 pt-3 border-t sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded text-sm hover:bg-gray-50 flex-1"
          >
            Huỷ
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!code.trim() || !name.trim() || save.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex-1"
          >
            {save.isPending ? "Đang lưu..." : "Lưu"}
          </button>
        </div>
        {save.error && (
          <div className="mt-3 text-sm text-red-600">
            {(save.error as any)?.response?.data?.detail ?? String(save.error)}
          </div>
        )}
      </div>
    </div>
  );
}
